import { useState, useEffect, useMemo } from "react";
import { Link, useParams, useNavigate, useSearchParams } from "react-router-dom";
import { shortTitle, shortDescription, stripBrand } from "@/utils/metaLength";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Calendar, Tag, MapPin, ArrowLeft, ArrowRight, Share2, MessageCircle, ChevronRight, Flower2, Heart, Diamond, ChevronDown, ChevronUp } from "lucide-react";
import Logo from "@/components/Logo";
import FooterCitiesCompact from "@/components/FooterCitiesCompact";
import FloatingWhatsAppBlog from "@/components/FloatingWhatsAppBlog";
import BlogMobileMenu from "@/components/BlogMobileMenu";
import { ArticleProductsShowcase } from "@/components/ArticleProductsShowcase";
import { InlineProductCards } from "@/components/InlineProductCards";
import { RosesQuantityGuide } from "@/components/RosesQuantityGuide";
import RelatedCityArticles from "@/components/RelatedCityArticles";
import { CITIES } from "@/cityConfig";
import { WEDDING_CITIES } from "@/weddingsConfig";
import LanguageSelector from "@/components/LanguageSelector";
import { trackPageView, trackClick } from "@/utils/tracker";
import { translateTag } from "@/utils/tagTranslations";
import { getLangFromUrl, setLangInUrl, useHreflang, updateCanonical, useAutoSyncLangToUrl } from "@/utils/seoLang";
import { extractArticleStructuredData } from "@/utils/articleStructuredData";

const API = "/api";

function ArticleJsonLd({ article, lang }) {
  const title = lang === "tr" && article.title_tr ? article.title_tr : (lang === "en" && article.title_en ? article.title_en : article.title_ru);
  const desc = lang === "tr" && article.meta_description_tr ? article.meta_description_tr : (lang === "en" && article.meta_description_en ? article.meta_description_en : article.meta_description_ru);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: title,
    description: desc || "",
    image: article.cover_image || "https://yuccaflower.com/hero.webp",
    datePublished: article.published_at,
    dateModified: article.updated_at,
    author: { "@type": "Organization", name: "YUCCA Flowers", url: "https://yuccaflower.com" },
    publisher: {
      "@type": "Organization",
      name: "YUCCA Flowers",
      logo: { "@type": "ImageObject", url: "https://yuccaflower.com/hero.webp" },
    },
    mainEntityOfPage: `https://yuccaflower.com/blog/${article.slug}`,
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
    />
  );
}

function BreadcrumbJsonLd({ article, title, cityName, lang }) {
  const items = [
    { name: lang === "tr" ? "Ana sayfa" : lang === "en" ? "Home" : "Главная", url: "https://yuccaflower.com/" },
    { name: lang === "tr" ? "Blog" : lang === "en" ? "Blog" : "Блог", url: "https://yuccaflower.com/blog" },
  ];
  if (article.city && cityName) {
    items.push({ name: cityName, url: `https://yuccaflower.com/${article.city}` });
  }
  items.push({ name: title, url: `https://yuccaflower.com/blog/${article.slug}` });

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, idx) => ({
      "@type": "ListItem",
      position: idx + 1,
      name: it.name,
      item: it.url,
    })),
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
    />
  );
}

export default function BlogArticlePage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [article, setArticle] = useState(null);
  const [relatedArticles, setRelatedArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  const availableLanguages = useMemo(() => {
    if (!article?.city) return ["ru", "en"];
    const langs = CITIES[article.city]?.languages || ["ru", "en"];
    return article.content_tr ? langs : langs.filter((l) => l !== "tr");
  }, [article]);

  const [lang, setLangState] = useState(() => getLangFromUrl(searchParams, availableLanguages));
  const [tagsExpanded, setTagsExpanded] = useState(false);

  // Re-check lang when availableLanguages updates (after article loads)
  useEffect(() => {
    const urlLang = searchParams.get("lang");
    if (urlLang && availableLanguages.includes(urlLang) && urlLang !== lang) {
      setLangState(urlLang);
    }
  }, [availableLanguages]);

  const setLang = (newLang) => {
    setLangState(newLang);
    setLangInUrl(newLang, searchParams, setSearchParams);
  };

  useAutoSyncLangToUrl(lang, searchParams, setSearchParams);

  // hreflang for article page
  useHreflang(`/blog/${slug}`, availableLanguages);

  // Set the canonical EAGERLY (independent of the async article fetch) so bots
  // that snapshot before the article loads don't keep the static homepage
  // canonical from index.html — the root cause of GSC "Alternate page with
  // proper canonical tag" on blog URLs.
  useEffect(() => {
    updateCanonical(`/blog/${slug}`, availableLanguages);
  }, [slug, lang, availableLanguages]);

  useEffect(() => {
    if (article) {
      const title = lang === "tr" && article.title_tr ? article.title_tr : (lang === "en" && article.title_en ? article.title_en : article.title_ru);
      const metaTitle = (lang === "tr" && article.meta_title_tr) || (lang === "en" && article.meta_title_en) || (lang === "ru" && article.meta_title_ru) || title;
      document.title = `${shortTitle(stripBrand(metaTitle), 60)} | YUCCA Flowers`;
      const desc = lang === "tr" && article.meta_description_tr ? article.meta_description_tr : (lang === "en" && article.meta_description_en ? article.meta_description_en : article.meta_description_ru);
      const setMeta = (sel, val) => { const el = document.querySelector(sel); if (el && val) el.setAttribute("content", val); };
      setMeta('meta[name="description"]', shortDescription(desc));

      // Per-article Open Graph & Twitter Card (otherwise stale homepage defaults leak)
      const img = article.cover_image
        ? (article.cover_image.startsWith("http") ? article.cover_image : `https://yuccaflower.com${article.cover_image}`)
        : "https://yuccaflower.com/og-banner.jpg";
      const url = `https://yuccaflower.com/blog/${slug}` + (lang !== "ru" ? `?lang=${lang}` : "");
      setMeta('meta[property="og:title"]', title);
      setMeta('meta[property="og:description"]', desc);
      setMeta('meta[property="og:image"]', img);
      setMeta('meta[property="og:url"]', url);
      setMeta('meta[property="og:type"]', "article");
      setMeta('meta[name="twitter:title"]', title);
      setMeta('meta[name="twitter:description"]', desc);
      setMeta('meta[name="twitter:image"]', img);

      document.documentElement.lang = lang;
      updateCanonical(`/blog/${slug}`, availableLanguages);
    }
  }, [lang, article, slug, availableLanguages]);

  useEffect(() => {
    const fetchArticle = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/blog/posts/${slug}`);
        setArticle(res.data);
        trackPageView(`/blog/${slug}`, res.data.city || "");
        // Fetch related articles from the same city (or general articles)
        const articleCity = res.data.city;
        try {
          let relUrl = articleCity
            ? `${API}/blog/posts?city=${articleCity}`
            : `${API}/blog/posts?city=`;
          const relRes = await axios.get(relUrl);
          const allArticles = relRes.data?.articles || relRes.data || [];
          let others = allArticles.filter(a => a.slug !== slug).slice(0, 4);
          // Fallback: if the city has too few sibling articles, top up with other posts
          if (others.length < 4) {
            try {
              const fbRes = await axios.get(`${API}/blog/posts?limit=12`);
              const fbAll = fbRes.data?.articles || fbRes.data || [];
              const seen = new Set([slug, ...others.map(a => a.slug)]);
              for (const a of fbAll) {
                if (others.length >= 4) break;
                if (seen.has(a.slug)) continue;
                seen.add(a.slug);
                others.push(a);
              }
            } catch {}
          }
          setRelatedArticles(others);
        } catch {}
        // Update page meta
        const title = lang === "tr" && res.data.title_tr ? res.data.title_tr : (lang === "en" && res.data.title_en ? res.data.title_en : res.data.title_ru);
        const metaTitle = (lang === "tr" && res.data.meta_title_tr) || (lang === "en" && res.data.meta_title_en) || (lang === "ru" && res.data.meta_title_ru) || title;
        document.title = `${shortTitle(stripBrand(metaTitle), 60)} | YUCCA Flowers`;
        const metaDesc = document.querySelector('meta[name="description"]');
        if (metaDesc) {
          const desc = lang === "tr" && res.data.meta_description_tr ? res.data.meta_description_tr : (lang === "en" && res.data.meta_description_en ? res.data.meta_description_en : res.data.meta_description_ru);
          if (desc) metaDesc.setAttribute("content", shortDescription(desc));
        }
      } catch {
        navigate("/blog", { replace: true });
      } finally {
        setLoading(false);
      }
    };
    fetchArticle();
  }, [slug, navigate, lang]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="w-10 h-10 border-2 border-pink-400 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!article) return null;

  const title = lang === "tr" && article.title_tr ? article.title_tr : (lang === "en" && article.title_en ? article.title_en : article.title_ru);
  const rawContent = lang === "tr" && article.content_tr ? article.content_tr : (lang === "en" && article.content_en ? article.content_en : article.content_ru);
  // Strip the first H1 from markdown (already shown as page title) and the inline duplicate of the cover image
  const content = rawContent
    .replace(/^#\s+[^\n]+\n*/, "")
    .replace(/^!\[[^\]]*\]\(([^)\s]+)\)[ \t]*\n*/m, (m, src) => (article.cover_image && src === article.cover_image ? "" : m));
  const cityData = article.city ? CITIES[article.city] : null;
  const cityName = cityData ? (lang === "tr" ? (cityData.nameTr || cityData.nameEn) : (lang === "en" ? cityData.nameEn : cityData.name)) : null;
  const weddingCity = article.city ? WEDDING_CITIES[article.city] : null;
  const weddingCityName = (lang === "en" ? weddingCity?.nameEn : weddingCity?.name) || cityName;
  const weddingCityPrep = (lang === "en" ? weddingCity?.namePrepositionalEn : weddingCity?.namePrepositional) || cityData?.namePrepositional || weddingCityName;
  const date = article.published_at
    ? new Date(article.published_at).toLocaleDateString(
        lang === "tr" ? "tr-TR" : (lang === "en" ? "en-US" : "ru-RU"),
        { year: "numeric", month: "long", day: "numeric" }
      )
    : "";

  const shareUrl = `https://yuccaflower.com/blog/${article.slug}`;

  return (
    <div className="min-h-screen bg-white" data-testid="blog-article-page">
      <ArticleJsonLd article={article} lang={lang} />
      <BreadcrumbJsonLd article={article} title={title} cityName={cityName} lang={lang} />
      {extractArticleStructuredData(rawContent, title).map((ld, i) => (
        <script key={ld["@type"] + i} type="application/ld+json" data-testid={`article-ld-${ld["@type"].toLowerCase()}`} dangerouslySetInnerHTML={{ __html: JSON.stringify(ld) }} />
      ))}

      {/* Header */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 flex items-center justify-between h-16">
          <Link to="/" className="flex items-center">
            <Logo variant="dark" />
          </Link>
          <div className="flex items-center gap-3">
            <Link
              to="/blog"
              className="hidden sm:inline text-sm font-bold text-pink-600"
              data-testid="article-blog-link"
            >
              {lang === "tr" ? "Blog" : (lang === "en" ? "Blog" : "Блог")}
            </Link>
            <LanguageSelector
              selectedLang={lang}
              onLangChange={setLang}
              isScrolled={true}
              availableLanguages={availableLanguages}
            />
            <BlogMobileMenu lang={lang} />
          </div>
        </div>
      </header>

      <article className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12" data-testid="article-content">
        {/* Breadcrumbs */}
        <nav className="flex items-center gap-1.5 text-xs text-gray-500 mb-5 flex-wrap" aria-label="Breadcrumb" data-testid="article-breadcrumbs">
          <Link to="/" className="hover:text-pink-600 transition-colors">
            {lang === "tr" ? "Ana sayfa" : lang === "en" ? "Home" : "Главная"}
          </Link>
          <ChevronRight className="w-3 h-3 text-gray-300" />
          <Link to="/blog" className="hover:text-pink-600 transition-colors">
            {lang === "tr" ? "Blog" : lang === "en" ? "Blog" : "Блог"}
          </Link>
          {article.city && cityName && (
            <>
              <ChevronRight className="w-3 h-3 text-gray-300" />
              <Link
                to={`/${article.city}${lang !== "ru" ? `?lang=${lang}` : ""}`}
                className="hover:text-pink-600 transition-colors"
                data-testid="breadcrumb-city-link"
              >
                {cityName}
              </Link>
            </>
          )}
          <ChevronRight className="w-3 h-3 text-gray-300" />
          <span className="text-gray-400 truncate max-w-[180px] sm:max-w-none">{title}</span>
        </nav>

        {/* Back link */}
        <Link
          to="/blog"
          className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-500 hover:text-pink-600 mb-6 transition-colors"
          data-testid="article-back-link"
        >
          <ArrowLeft className="w-4 h-4" />
          {lang === "tr" ? "Blog'a dön" : (lang === "en" ? "Back to blog" : "Назад к блогу")}
        </Link>

        {/* Meta */}
        <div className="flex items-center gap-2 mb-4 flex-wrap" data-testid="article-tags">
          {cityName && (
            <span className="inline-flex items-center gap-1 text-xs font-semibold text-pink-600 bg-pink-50 px-2.5 py-1 rounded-full">
              <MapPin className="w-3 h-3" />
              {cityName}
            </span>
          )}
          {(() => {
            const allTags = article.tags || [];
            const VISIBLE = 4;
            const visibleTags = tagsExpanded ? allTags : allTags.slice(0, VISIBLE);
            const hiddenCount = allTags.length - VISIBLE;
            return (
              <>
                {visibleTags.map(tag => (
                  <Link
                    key={tag}
                    to={`/blog?tag=${encodeURIComponent(tag)}`}
                    className="text-xs font-medium text-gray-500 bg-gray-100 px-2.5 py-1 rounded-full hover:bg-gray-200 transition-colors"
                  >
                    {translateTag(tag, lang)}
                  </Link>
                ))}
                {hiddenCount > 0 && (
                  <button
                    type="button"
                    onClick={() => setTagsExpanded(v => !v)}
                    className="inline-flex items-center gap-1 text-xs font-semibold text-pink-600 bg-pink-50 hover:bg-pink-100 px-2.5 py-1 rounded-full transition-colors"
                    data-testid="tags-toggle-btn"
                    aria-expanded={tagsExpanded}
                  >
                    {tagsExpanded ? (
                      <>
                        {lang === "en" ? "Show less" : lang === "tr" ? "Daha az" : "Свернуть"}
                        <ChevronUp className="w-3 h-3" />
                      </>
                    ) : (
                      <>
                        +{hiddenCount} {lang === "en" ? "more" : lang === "tr" ? "daha" : "ещё"}
                        <ChevronDown className="w-3 h-3" />
                      </>
                    )}
                  </button>
                )}
              </>
            );
          })()}
          {date && (
            <span className="flex items-center gap-1.5 text-xs text-gray-400 ml-1">
              <Calendar className="w-3 h-3" />
              {date}
            </span>
          )}
        </div>

        {/* Title */}
        <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6 leading-tight" data-testid="article-title">
          {title}
        </h1>

        {/* Top CTA - compact (Style 2) */}
        {cityData && (
          <Link
            to={`/${article.city}${lang !== "ru" ? `?lang=${lang}` : ""}`}
            className="group flex items-center gap-3 sm:gap-4 mb-8 p-4 bg-gradient-to-r from-pink-50/70 to-rose-50/70 hover:from-pink-50 hover:to-rose-50 border border-pink-100 rounded-xl transition-all"
            data-testid="article-top-cta"
          >
            <div className="shrink-0 w-10 h-10 rounded-full bg-white border border-pink-100 flex items-center justify-center">
              <Flower2 className="w-5 h-5 text-pink-500" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-gray-900 leading-tight">
                {lang === "tr"
                  ? `${cityName} - cicek katalogu`
                  : lang === "en"
                  ? `Flower catalog in ${cityName}`
                  : `Каталог цветов ${cityData.namePrepositional || cityName}`}
              </div>
              <div className="text-xs text-gray-500 mt-0.5">
                {lang === "tr"
                  ? "Ayni gun teslimat - gondermeden once fotograf"
                  : lang === "en"
                  ? "Same-day delivery - photo before sending"
                  : "Доставка в день заказа · фото перед отправкой"}
              </div>
            </div>
            <ArrowLeft className="w-4 h-4 text-pink-500 rotate-180 shrink-0 group-hover:translate-x-1 transition-transform" />
          </Link>
        )}

        {/* Wedding CTA — compact (only if city has a wedding landing page) */}
        {article.city && WEDDING_CITIES[article.city] && (
          <Link
            to={`/weddings/${article.city}${lang !== "ru" ? `?lang=${lang}` : ""}`}
            className="group flex items-center gap-3 sm:gap-4 mb-8 p-4 bg-[#0A0807] hover:bg-[#1A1410] border border-[#F5C77A]/30 hover:border-[#F5C77A]/70 rounded-xl transition-all"
            data-testid="article-wedding-cta-top"
          >
            <div className="shrink-0 w-10 h-10 rounded-full bg-[#F5C77A]/15 border border-[#F5C77A]/40 flex items-center justify-center">
              <Heart className="w-4.5 h-4.5 text-[#F5C77A]" strokeWidth={1.5} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] tracking-[0.25em] text-[#F5C77A] font-light mb-0.5">
                {lang === "en" ? "PREMIUM WEDDING FLORISTRY" : lang === "tr" ? "PREMIUM DUGUN CICEKLERI" : "ПРЕМИУМ СВАДЕБНАЯ ФЛОРИСТИКА"}
              </div>
              <div className="text-sm font-medium text-white leading-tight"
                   style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500, fontSize: "1.05rem" }}>
                {lang === "en" ? `Wedding design in ${weddingCityName}` : lang === "tr" ? `${weddingCityName}'da dugun tasarimi` : `Свадьба ${weddingCityPrep}`} <span className="text-[#F5C77A]/80 text-xs">— {(WEDDING_CITIES[article.city]?.seo?.[lang]?.packages?.[0]?.priceFrom) || (WEDDING_CITIES[article.city]?.seo?.ru?.packages?.[0]?.priceFrom) || (lang === "en" ? "from €2,500" : "от €2 500")}</span>
              </div>
            </div>
            <ArrowRight className="w-4 h-4 text-[#F5C77A] shrink-0 group-hover:translate-x-1 transition-transform" />
          </Link>
        )}

        {/* Cover Image */}
        {article.cover_image && (
          <div className="rounded-2xl overflow-hidden mb-8 bg-gray-100">
            <img
              src={article.cover_image}
              alt={title}
              className="w-full object-cover max-h-[500px]"
            />
          </div>
        )}

        {/* Content */}
        <div className="prose prose-lg prose-gray max-w-none prose-headings:font-bold prose-headings:text-gray-900 prose-a:text-pink-600 prose-a:no-underline hover:prose-a:underline prose-img:rounded-xl" data-testid="article-body">
          {content.split(/\[\[PRODUCTS:([^\]]+)\]\]/).map((chunk, idx) => {
            // Odd indices are the captured product-id lists from the marker
            if (idx % 2 === 1) {
              const ids = chunk.split(",").map((s) => s.trim()).filter(Boolean);
              return (
                <InlineProductCards
                  key={`prods-${idx}`}
                  ids={ids}
                  city={article.city}
                  cityData={cityData}
                  cityName={cityName}
                  lang={lang}
                />
              );
            }
            if (!chunk.trim()) return null;
            return (
              <ReactMarkdown key={`md-${idx}`} remarkPlugins={[remarkGfm]} components={{
                a: ({ href, children, ...props }) => {
                  const isExternal = /^https?:\/\//i.test(href || "");
                  return isExternal ? (
                    <a href={href} target="_blank" rel="noopener noreferrer" {...props}>{children}</a>
                  ) : (
                    <a href={href} {...props}>{children}</a>
                  );
                },
              }}>{chunk}</ReactMarkdown>
            );
          })}
        </div>

        {/* First-order discount promo (blog referral) */}
        <div className="mt-10 rounded-2xl bg-gradient-to-br from-[#C2185B] to-[#E91E63] p-6 sm:p-8 text-white shadow-lg" data-testid="article-promo-discount">
          <p className="text-[11px] tracking-[0.28em] font-semibold text-pink-100 mb-2">
            {lang === "tr" ? "OKUYUCULARA OZEL" : lang === "en" ? "READER EXCLUSIVE" : "ЧИТАТЕЛЯМ БЛОГА"}
          </p>
          <h3 className="text-2xl sm:text-3xl font-bold mb-3 leading-tight">
            {lang === "tr" ? "İlk siparişinize %5 indirim" : lang === "en" ? "5% off your first order" : "Скидка 5% на первый заказ"}
          </h3>
          <p className="text-pink-50 text-sm sm:text-base mb-5 max-w-xl">
            {lang === "tr"
              ? "Blogumuzdan geldiğiniz için ilk siparişinizde %5 indirim. WhatsApp'ta sipariş verirken floriste BLOG5 kodunu söyleyin."
              : lang === "en"
              ? "Because you came from our blog, enjoy 5% off your first order. Just mention promo code BLOG5 to the florist on WhatsApp."
              : "Вы пришли из нашего блога — держите 5% скидку на первый заказ. Назовите флористу промокод BLOG5 при оформлении в WhatsApp."}
          </p>
          <div className="flex flex-wrap items-center gap-4">
            <div className="inline-flex items-center gap-2 bg-white/15 border border-dashed border-white/60 rounded-xl px-5 py-3">
              <span className="text-[10px] tracking-[0.2em] text-pink-100">
                {lang === "tr" ? "PROMOSYON KODU" : lang === "en" ? "PROMO CODE" : "ПРОМОКОД"}
              </span>
              <span className="text-xl font-extrabold tracking-widest" data-testid="promo-code">BLOG5</span>
            </div>
            <a
              href={`https://wa.me/905316224378?text=${encodeURIComponent(
                lang === "tr"
                  ? `Merhaba! YUCCA blogundan geliyorum ("${title}"). İlk siparişimde BLOG5 koduyla %5 indirim kullanmak istiyorum.`
                  : lang === "en"
                  ? `Hello! I'm from the YUCCA blog (article "${title}"). I'd like to order a bouquet with the BLOG5 5% first-order discount.`
                  : `Здравствуйте! Я из блога YUCCA (статья «${title}»). Хочу заказать букет со скидкой 5% на первый заказ по промокоду BLOG5.`
              )}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-white text-[#C2185B] px-6 py-3 rounded-full font-bold hover:shadow-xl hover:scale-[1.02] transition-all"
              data-testid="promo-whatsapp-btn"
            >
              <MessageCircle className="w-5 h-5" />
              {lang === "tr" ? "WhatsApp'ta al" : lang === "en" ? "Claim on WhatsApp" : "Получить скидку в WhatsApp"}
            </a>
          </div>
        </div>

        {article.slug?.startsWith("rozy-") && (
          <RosesQuantityGuide lang={lang} city={article.city} cityName={cityName} />
        )}

        {/* Popular bouquets from the city catalog (conversion block) */}
        {cityData && (
          <ArticleProductsShowcase
            city={article.city}
            cityData={cityData}
            cityName={cityName}
            lang={lang}
          />
        )}

        {/* CTA: Order flowers */}
        {cityData && (
          <div className="mt-12 bg-gradient-to-r from-pink-50 to-rose-50 rounded-2xl p-6 sm:p-8 border border-pink-100" data-testid="article-cta">
            <h3 className="text-xl font-bold text-gray-900 mb-2">
              {lang === "tr"
                ? `${cityName} icin cicek siparis edin`
                : lang === "en"
                ? `Order flowers in ${cityName}`
                : `Заказать цветы ${cityData.namePrepositional}`}
            </h3>
            <p className="text-gray-600 text-sm mb-4">
              {lang === "tr"
                ? "Ayni gun teslimat. Gondermeden once fotograf."
                : lang === "en"
                ? "Same-day delivery. Photo before sending."
                : "Доставка в день заказа. Фото перед отправкой."}
            </p>
            <div className="flex flex-wrap gap-3">
              <Link
                to={`/${article.city}`}
                className="inline-flex items-center gap-2 bg-gradient-to-r from-pink-500 to-rose-500 text-white px-6 py-3 rounded-full font-bold hover:shadow-lg transition-all"
                data-testid="article-catalog-link"
              >
                {lang === "tr" ? "Katalogu goruntule" : lang === "en" ? "View catalog" : "Смотреть каталог"}
                <ArrowLeft className="w-4 h-4 rotate-180" />
              </Link>
              <a
                href="https://wa.me/905316224378"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-[#25d366] text-white px-6 py-3 rounded-full font-bold hover:shadow-lg transition-all"
              >
                <MessageCircle className="w-4 h-4" />
                WhatsApp
              </a>
            </div>
          </div>
        )}

        {/* Wedding Premium CTA — dramatic (only if city has a wedding landing) */}
        {article.city && WEDDING_CITIES[article.city] && (
          <div className="mt-8 relative bg-[#0A0807] text-white rounded-2xl overflow-hidden" data-testid="article-wedding-cta-bottom">
            <div className="absolute inset-0 opacity-[0.06] pointer-events-none"
                 style={{ backgroundImage: "radial-gradient(#D4B896 0.5px, transparent 0.5px)", backgroundSize: "22px 22px" }} />
            <div className="absolute -top-20 -right-20 w-[300px] h-[300px] rounded-full bg-[#F5C77A]/15 blur-3xl pointer-events-none" />

            <div className="relative px-6 sm:px-10 py-10 sm:py-12 text-center">
              <div className="flex items-center justify-center gap-2 sm:gap-3 mb-5">
                <span className="w-8 sm:w-10 h-px bg-[#F5C77A]" />
                <Diamond className="w-3 h-3 text-[#F5C77A]" />
                <span className="w-8 sm:w-10 h-px bg-[#F5C77A]" />
              </div>
              <p className="text-[10px] tracking-[0.3em] text-[#F5C77A] mb-3 font-light">
                {lang === "en" ? "WEDDING FLORISTRY" : lang === "tr" ? "DUGUN CICEKLERI" : "СВАДЕБНАЯ ФЛОРИСТИКА"}
              </p>
              <h3 className="text-2xl sm:text-3xl text-white mb-3 sm:mb-4 leading-tight"
                  style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 400 }}>
                {lang === "en" ? "Planning a wedding" : lang === "tr" ? "Dugun planliyor musunuz" : "Планируете свадьбу"} <span className="italic text-[#F5C77A]">{lang === "tr" ? weddingCityName : weddingCityPrep}?</span>
              </h3>
              <p className="text-sm text-white/65 font-light leading-relaxed mb-7 max-w-md mx-auto">
                {lang === "en"
                  ? `Premium turnkey wedding design — from the bridal bouquet to banquet. From €2,500. Reply within 24 hours.`
                  : lang === "tr"
                  ? `Premium dugun cicekleri ve tasarimi - gelin buketinden banket dekoruna. €2,500'den itibaren. 24 saatte yanit.`
                  : `Премиум оформление свадьбы под ключ — от букета невесты до банкета. От €2 500. Ответ за 24 часа.`}
              </p>
              <Link
                to={`/weddings/${article.city}${lang !== "ru" ? `?lang=${lang}` : ""}`}
                className="group inline-flex items-center gap-2 sm:gap-3 px-8 sm:px-10 py-3.5 sm:py-4 bg-gradient-to-r from-[#FFE5A8] via-[#F5C77A] to-[#E0A954] hover:from-[#FFEDB8] hover:to-[#E8B86C] text-[#0A0807] text-[11px] sm:text-[12px] tracking-[0.22em] sm:tracking-[0.28em] font-semibold transition-all duration-500 rounded-sm shadow-[0_12px_40px_-10px_rgba(245,199,122,0.55)] hover:shadow-[0_18px_55px_-10px_rgba(245,199,122,0.75)] hover:-translate-y-0.5"
                data-testid="article-wedding-cta-link"
              >
                <Heart className="w-4 h-4" strokeWidth={2} />
                <span>{lang === "en" ? "OPEN WEDDING PAGE" : lang === "tr" ? "DUGUN SAYFASI" : "ПЕРЕЙТИ К СВАДЬБАМ"}</span>
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </div>
          </div>
        )}

        {/* Share */}
        <div className="mt-8 flex items-center gap-3 pt-6 border-t border-gray-100">
          <Share2 className="w-4 h-4 text-gray-400" />
          <span className="text-sm text-gray-500">{lang === "tr" ? "Paylas:" : lang === "en" ? "Share:" : "Поделиться:"}</span>
          <a
            href={`https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(title)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm font-medium text-blue-500 hover:underline"
          >
            Telegram
          </a>
          <a
            href={`https://wa.me/?text=${encodeURIComponent(title + " " + shareUrl)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm font-medium text-green-600 hover:underline"
          >
            WhatsApp
          </a>
        </div>
      </article>

      {/* Related Articles - Internal Linking */}
      {relatedArticles.length > 0 && (
        <section className="max-w-4xl mx-auto px-4 sm:px-6 mt-12" data-testid="related-articles">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            {lang === "tr" ? "Ayrica okuyun" : lang === "en" ? "Read also" : "Читайте также"}
          </h2>
          <div className="grid gap-3">
            {relatedArticles.map((a) => {
              const t = lang === "tr" && a.title_tr ? a.title_tr : lang === "en" && a.title_en ? a.title_en : a.title_ru;
              const ex = lang === "tr" && a.excerpt_tr ? a.excerpt_tr : lang === "en" && a.excerpt_en ? a.excerpt_en : a.excerpt_ru;
              return (
                <Link
                  key={a.slug}
                  to={`/blog/${a.slug}${lang !== "ru" ? `?lang=${lang}` : ""}`}
                  className="flex gap-4 p-4 bg-white rounded-xl border border-gray-100 hover:border-gray-300 hover:shadow-sm transition-all group"
                  data-testid={`related-${a.slug}`}
                >
                  {a.cover_image && (
                    <img src={a.cover_image} alt={t} className="w-20 h-20 rounded-lg object-cover shrink-0" loading="lazy" />
                  )}
                  <div className="min-w-0">
                    <h3 className="text-sm font-medium text-gray-900 group-hover:text-pink-600 transition-colors line-clamp-2">{t}</h3>
                    {ex && <p className="text-xs text-gray-500 mt-1 line-clamp-2">{ex}</p>}
                  </div>
                </Link>
              );
            })}
          </div>
          {article?.city && (
            <Link
              to={`/${article.city}${lang !== "ru" ? `?lang=${lang}` : ""}`}
              className="inline-block mt-4 text-sm text-pink-600 hover:text-pink-700 font-medium"
            >
              {lang === "tr" ? `${cityName || article.city} cicek katalogu` : lang === "en" ? `${cityName || article.city} flower catalog` : `Каталог цветов — ${cityName || article.city}`} →
            </Link>
          )}
        </section>
      )}

      {/* Related articles from neighbouring cities in the same region (cluster) */}
      {article?.city && <RelatedCityArticles citySlug={article.city} lang={lang} />}

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-10 mt-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <Logo variant="light" />
          <p className="text-gray-400 text-sm mt-4">
            &copy; 2024 YUCCA Flowers. {lang === "tr" ? "Tum haklar saklidir." : lang === "en" ? "All rights reserved." : "Все права защищены."}
          </p>
          <div className="flex items-center justify-center gap-4 mt-3 text-sm">
            <Link to="/" className="text-gray-500 hover:text-gray-300 transition-colors">
              {lang === "tr" ? "Ana sayfa" : lang === "en" ? "Home" : "Главная"}
            </Link>
            <Link to="/blog" className="text-gray-500 hover:text-gray-300 transition-colors">
              {lang === "tr" ? "Blog" : lang === "en" ? "Blog" : "Блог"}
            </Link>
          </div>
          <FooterCitiesCompact lang={lang} />
        </div>
      </footer>
      <FloatingWhatsAppBlog lang={lang} />
    </div>
  );
}
